import { classes } from "lib/actions/util";
import { useState } from "react";
import {
  useFloating,
  useHover,
  useDismiss,
  useInteractions,
  useRole,
  FloatingPortal,
  autoUpdate,
  offset,
  flip,
  shift,
  safePolygon,
} from "@floating-ui/react";
import { AnimatePresence, motion } from "motion/react";
import ChevronRightSvg from "components/icons/24/actions/chevron-right.svg";
import Dropdown from "./Dropdown";

export type ActionMenuItem = {
  leftIcon: React.ReactNode;
  text: string;
  textClasses?: string;
  rightIcon?: React.ReactNode;
  onClick?: () => void;
  isDisabled?: boolean;
  isHidden?: boolean;
  showTooltip?: boolean;
  tooltipContent?: string;
  disableCloseOnClick?: boolean;
  submenu?: ActionMenuItem[];
};

export default function DropdownActionMenu(
  p: Readonly<{
    buttonContent: React.ReactNode;
    className?: string;
    items: ActionMenuItem[];
    dropdownWidth?: number;
    onOpen?: () => void;
  }>,
) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    if (!isOpen) p.onOpen?.();
    setIsOpen((prev) => !prev);
  };

  const dropdownWidth = p.dropdownWidth ?? 210;
  const items = p.items.filter((i) => !i.isHidden);

  return (
    <Dropdown
      dropdownWidth={dropdownWidth}
      variant="simple"
      placement="right-start"
      isOpen={isOpen}
      toggleOpen={toggleOpen}
      buttonContent={p.buttonContent}
      className={p.className}
      dropdownContent={
        <div>
          {items.map((item, index) => (
            <ActionMenuItemRow
              key={item.text}
              item={item}
              isLast={index === items.length - 1}
              onClose={toggleOpen}
              dropdownWidth={dropdownWidth}
            />
          ))}
        </div>
      }
    />
  );
}

function ActionMenuItemRow(
  p: Readonly<{
    item: ActionMenuItem;
    isLast: boolean;
    dropdownWidth: number;
    onClose(): void;
  }>,
) {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hasSubmenu = !!p.item.submenu?.length;
  const showTooltip = !!p.item.showTooltip;

  const {
    refs: submenuRefs,
    floatingStyles: submenuFloatingStyles,
    context: submenuContext,
  } = useFloating({
    open: isSubmenuOpen,
    onOpenChange: setIsSubmenuOpen,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 1 }), flip()],
  });

  const {
    refs: tooltipRefs,
    floatingStyles: tooltipFloatingStyles,
    context: tooltipContext,
  } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: "right",
    whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 10 }), flip(), shift({ padding: 8 })],
  });

  const submenuHover = useHover(submenuContext, {
    delay: { open: 100, close: 150 },
    handleClose: safePolygon(),
    enabled: hasSubmenu,
  });
  const submenuDismiss = useDismiss(submenuContext, { enabled: hasSubmenu });
  const {
    getReferenceProps: getSubmenuReferenceProps,
    getFloatingProps: getSubmenuFloatingProps,
  } = useInteractions([submenuHover, submenuDismiss]);

  const tooltipHover = useHover(tooltipContext, {
    delay: { open: 200, close: 100 },
    enabled: showTooltip,
    move: false,
  });
  const tooltipDismiss = useDismiss(tooltipContext, { enabled: showTooltip });
  const tooltipRole = useRole(tooltipContext, { role: "tooltip" });
  const {
    getReferenceProps: getTooltipReferenceProps,
    getFloatingProps: getTooltipFloatingProps,
  } = useInteractions([tooltipHover, tooltipDismiss, tooltipRole]);

  const buttonContent = (
    <button
      onClick={() => {
        if (!hasSubmenu) {
          p.item.onClick?.();
          if (!p.item.disableCloseOnClick) p.onClose();
        }
      }}
      className={classes(
        "flex w-full items-center px-3 py-2.5 hover:bg-neutral-50 disabled:bg-neutral-50 disabled:opacity-50",
        p.item.textClasses,
      )}
      disabled={p.item.isDisabled}
    >
      <div className="h-full w-6">{p.item.leftIcon}</div>
      <p className="flex-1 grow pl-4 text-left font-light">{p.item.text}</p>
      <div className="h-full w-6">
        {hasSubmenu ? (
          <ChevronRightSvg className="aspect-square h-6 text-gray-400" />
        ) : (
          p.item.rightIcon
        )}
      </div>
    </button>
  );

  return (
    <>
      <div
        ref={(node) => {
          tooltipRefs.setReference(node);
          if (hasSubmenu) submenuRefs.setReference(node);
        }}
        {...getTooltipReferenceProps(
          hasSubmenu ? getSubmenuReferenceProps() : {},
        )}
      >
        {buttonContent}
        {!p.isLast && <hr className="mx-3" />}
        {hasSubmenu && (
          <AnimatePresence initial={false}>
            {isSubmenuOpen && (
              <FloatingPortal>
                <motion.div
                  ref={submenuRefs.setFloating}
                  style={{ ...submenuFloatingStyles, width: p.dropdownWidth }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  className="z-50 select-none overflow-x-hidden rounded-md bg-white text-gray-900 shadow-md ring-1 ring-black ring-opacity-5"
                  {...getSubmenuFloatingProps()}
                >
                  {p.item.submenu
                    .filter((sub) => !sub.isHidden)
                    .map((subItem, subIndex, subArray) => (
                      <ActionMenuItemRow
                        key={subItem.text}
                        item={subItem}
                        isLast={subIndex === subArray.length - 1}
                        onClose={p.onClose}
                        dropdownWidth={p.dropdownWidth}
                      />
                    ))}
                </motion.div>
              </FloatingPortal>
            )}
          </AnimatePresence>
        )}
      </div>
      {isTooltipOpen && showTooltip && p.item.tooltipContent && (
        <FloatingPortal>
          <div
            ref={tooltipRefs.setFloating}
            style={tooltipFloatingStyles}
            {...getTooltipFloatingProps()}
            className="pointer-events-none z-50 rounded-md bg-white px-2 py-1 text-sm text-gray-900 shadow-md ring-1 ring-black/10"
          >
            <div className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-l border-t border-black/10 bg-white" />
            {p.item.tooltipContent}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
